import React, { useState, useRef, useCallback } from 'react';
import { useProjects } from '../../hooks/useProjects';
import { useCreateInvoice, useUpdateInvoice } from '../../hooks/useInvoices';
import { useClients } from '../../hooks/useClients';
import { useEffectiveCompanyId } from '../../hooks/useEffectiveCompany';
import { validateRut, formatRut } from '../../utils/rutValidator';
import ClientSelector from '../clients/ClientSelector';
import type { Invoice, InvoiceCreate, InvoiceStatus } from '../../types';

interface InvoiceFormProps {
  invoice?: Invoice;
  onSuccess?: () => void;
  onCancel?: () => void;
}

const InvoiceForm: React.FC<InvoiceFormProps> = ({ invoice, onSuccess, onCancel }) => {
  const isEditing = !!invoice;
  
  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('es-CL', {
      style: 'currency',
      currency: 'CLP',
    }).format(amount);
  };
  
  // Form state
  const [projectId, setProjectId] = useState(invoice?.projectId || '');
  const [invoiceNumber, setInvoiceNumber] = useState(invoice?.invoiceNumber || '');
  const [invoiceDate, setInvoiceDate] = useState(invoice?.invoiceDate || new Date().toISOString().split('T')[0]);
  const [dueDate, setDueDate] = useState(invoice?.dueDate || '');
  const [clientId, setClientId] = useState<string | null>(invoice?.clientId || null);
  const [clientRut, setClientRut] = useState(invoice?.clientRut || '');
  const [clientName, setClientName] = useState(invoice?.clientName || '');
  const [totalAmount, setTotalAmount] = useState(invoice?.totalAmount?.toString() || '');
  const [status, setStatus] = useState<InvoiceStatus>(invoice?.status || 'pending');
  const [description, setDescription] = useState(invoice?.description || '');
  const [issuerName, setIssuerName] = useState(invoice?.issuerName || '');
  
  // Track whether the user manually changed the client selection
  const clientManuallyChanged = useRef(false);
  
  // Auth
  const companyId = useEffectiveCompanyId();
  const showIssuerField = companyId === '776954438';
  
  // Validation errors
  const [errors, setErrors] = useState<Record<string, string>>({});
  
  // Queries
  const { data: projects, isLoading: projectsLoading } = useProjects();
  const { data: clientsData } = useClients({ active: true });
  
  // Mutations
  const createInvoice = useCreateInvoice();
  const updateInvoice = useUpdateInvoice();
  
  // Handle project change: pre-select client from project if user hasn't manually changed it
  const handleProjectChange = useCallback((newProjectId: string) => {
    setProjectId(newProjectId);
    handleFieldChange('projectId', newProjectId);
    
    if (!newProjectId) return;
    
    const selectedProject = projects?.find(p => p.projectId === newProjectId);
    if (!selectedProject) return;
    
    if (selectedProject.clientId) {
      // Project has a client — pre-select it (regardless of manual flag, since user is actively changing project)
      if (!clientManuallyChanged.current) {
        setClientId(selectedProject.clientId);
        // Populate clientRut and clientName from the clients list
        const client = clientsData?.items?.find(c => c.clientId === selectedProject.clientId);
        if (client) {
          setClientRut(formatRut(client.rut));
          setClientName(client.name);
          setErrors(prev => ({ ...prev, clientRut: '', clientName: '' }));
        }
      }
    } else {
      // Project has no client — clear selection only if user hasn't manually chosen one
      if (!clientManuallyChanged.current) {
        setClientId(null);
        setClientRut('');
        setClientName('');
      }
    }
  }, [projects, clientsData]);
  
  // Handle client selector change: populate clientRut and clientName
  const handleClientSelectorChange = useCallback((selectedClientId: string | null) => {
    clientManuallyChanged.current = true;
    setClientId(selectedClientId);
    
    if (selectedClientId) {
      const client = clientsData?.items?.find(c => c.clientId === selectedClientId);
      if (client) {
        setClientRut(formatRut(client.rut));
        setClientName(client.name);
        setErrors(prev => ({ ...prev, clientRut: '', clientName: '' }));
      }
    } else {
      // User cleared the selection — clear the fields
      setClientRut('');
      setClientName('');
    }
  }, [clientsData]);
  
  // Real-time validation
  const validateField = (field: string, value: string): string => {
    switch (field) {
      case 'projectId':
        return value ? '' : 'Proyecto es requerido';
      case 'invoiceNumber':
        return value.trim() ? '' : 'Número de factura es requerido';
      case 'invoiceDate':
        return value ? '' : 'Fecha de emisión es requerida';
      case 'dueDate':
        if (!value) return 'Fecha de vencimiento es requerida';
        if (invoiceDate && value < invoiceDate) return 'Fecha de vencimiento debe ser posterior a la emisión';
        return '';
      case 'clientRut':
        if (!value.trim()) return 'RUT del cliente es requerido';
        const rutValidation = validateRut(value);
        return rutValidation.valid ? '' : (rutValidation.error || 'RUT inválido');
      case 'clientName':
        return value.trim() ? '' : 'Razón social es requerida';
      case 'totalAmount':
        if (!value) return 'Monto es requerido';
        const numValue = parseFloat(value);
        if (isNaN(numValue) || numValue <= 0) return 'Monto debe ser mayor que 0';
        if (!/^\d+(\.\d{1,2})?$/.test(value)) return 'Monto debe tener máximo 2 decimales';
        return '';
      case 'description':
        return value.trim() ? '' : 'Descripción es requerida';
      default:
        return '';
    }
  };
  
  const handleFieldChange = (field: string, value: string) => {
    const error = validateField(field, value);
    setErrors(prev => ({ ...prev, [field]: error }));
  };
  
  // Format RUT on blur
  const handleRutBlur = () => {
    const validation = validateRut(clientRut);
    if (validation.valid && validation.cleanRut) {
      setClientRut(formatRut(validation.cleanRut));
    }
  };
  
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {
      projectId: validateField('projectId', projectId),
      invoiceNumber: validateField('invoiceNumber', invoiceNumber),
      invoiceDate: validateField('invoiceDate', invoiceDate),
      dueDate: validateField('dueDate', dueDate),
      clientRut: validateField('clientRut', clientRut),
      clientName: validateField('clientName', clientName),
      totalAmount: validateField('totalAmount', totalAmount),
      description: validateField('description', description),
    };
    
    setErrors(newErrors);
    return !Object.values(newErrors).some(error => error !== '');
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    const rutValidation = validateRut(clientRut);
    if (!rutValidation.valid || !rutValidation.cleanRut) {
      return;
    }
    
    // Calculate net amount and tax (IVA 19%)
    const total = parseFloat(totalAmount);
    const net = Math.round((total / 1.19) * 100) / 100; // Round to 2 decimals
    const tax = Math.round((total - net) * 100) / 100; // Round to 2 decimals
    
    const invoiceData: InvoiceCreate = {
      projectId,
      invoiceNumber: invoiceNumber.trim(),
      invoiceDate,
      dueDate,
      clientId: clientId || undefined,
      clientRut: rutValidation.cleanRut,
      clientName: clientName.trim(),
      netAmount: net,
      taxAmount: tax,
      totalAmount: total,
      issuerName: issuerName || undefined,
      status,
      description: description.trim(),
    };
    
    try {
      if (isEditing) {
        await updateInvoice.mutateAsync({
          invoiceId: invoice.invoiceId,
          data: invoiceData,
        });
      } else {
        await createInvoice.mutateAsync(invoiceData);
      }
      onSuccess?.();
    } catch (error) {
      console.error('Error saving invoice:', error);
    }
  };
  
  const isSaving = createInvoice.isLoading || updateInvoice.isLoading;
  
  if (projectsLoading) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }
  
  return (
    <form onSubmit={handleSubmit} className="space-y-6 max-w-4xl mx-auto">
      {/* Empresa Emisora - solo para SOLARMAQENERGY */}
      {showIssuerField && (
        <div>
          <label htmlFor="issuerName" className="block text-sm font-medium text-gray-700 mb-1">
            Empresa Emisora
          </label>
          <select
            id="issuerName"
            value={issuerName}
            onChange={(e) => setIssuerName(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Seleccionar empresa emisora</option>
            <option value="SOLARMAQENERGY">SOLARMAQENERGY</option>
            <option value="JCA">JCA</option>
          </select>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Project */}
        <div>
          <label htmlFor="projectId" className="block text-sm font-medium text-gray-700 mb-1">
            Proyecto <span className="text-red-500">*</span>
          </label>
          <select
            id="projectId"
            value={projectId}
            onChange={(e) => {
              handleProjectChange(e.target.value);
            }}
            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              errors.projectId ? 'border-red-500' : 'border-gray-300'
            }`}
            disabled={isEditing}
          >
            <option value="">Seleccionar proyecto</option>
            {projects?.map((project) => (
              <option key={project.projectId} value={project.projectId}>
                {project.name}{project.description ? ` — ${project.description}` : ''}{project.orderNumber ? ` (OC: ${project.orderNumber})` : ''}
              </option>
            ))}
          </select>
          {errors.projectId && (
            <p className="mt-1 text-sm text-red-600">{errors.projectId}</p>
          )}
        </div>
        
        {/* Invoice Number */}
        <div>
          <label htmlFor="invoiceNumber" className="block text-sm font-medium text-gray-700 mb-1">
            Número de Factura <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="invoiceNumber"
            value={invoiceNumber}
            onChange={(e) => {
              setInvoiceNumber(e.target.value);
              handleFieldChange('invoiceNumber', e.target.value);
            }}
            placeholder="Ej: 12345"
            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              errors.invoiceNumber ? 'border-red-500' : 'border-gray-300'
            }`}
          />
          {errors.invoiceNumber && (
            <p className="mt-1 text-sm text-red-600">{errors.invoiceNumber}</p>
          )}
        </div>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Issue Date */}
        <div>
          <label htmlFor="invoiceDate" className="block text-sm font-medium text-gray-700 mb-1">
            Fecha de Emisión <span className="text-red-500">*</span>
          </label>
          <input
            type="date"
            id="invoiceDate"
            value={invoiceDate}
            onChange={(e) => {
              setInvoiceDate(e.target.value);
              handleFieldChange('invoiceDate', e.target.value);
              if (dueDate) {
                handleFieldChange('dueDate', dueDate);
              }
            }}
            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              errors.invoiceDate ? 'border-red-500' : 'border-gray-300'
            }`}
          />
          {errors.invoiceDate && (
            <p className="mt-1 text-sm text-red-600">{errors.invoiceDate}</p>
          )}
        </div>
        
        {/* Due Date */}
        <div>
          <label htmlFor="dueDate" className="block text-sm font-medium text-gray-700 mb-1">
            Fecha de Vencimiento <span className="text-red-500">*</span>
          </label>
          <input
            type="date"
            id="dueDate"
            value={dueDate}
            onChange={(e) => {
              setDueDate(e.target.value);
              handleFieldChange('dueDate', e.target.value);
            }}
            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              errors.dueDate ? 'border-red-500' : 'border-gray-300'
            }`}
          />
          {errors.dueDate && (
            <p className="mt-1 text-sm text-red-600">{errors.dueDate}</p>
          )}
        </div>
        
        {/* Status */}
        <div>
          <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-1">
            Estado <span className="text-red-500">*</span>
          </label>
          <select
            id="status"
            value={status}
            onChange={(e) => setStatus(e.target.value as InvoiceStatus)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="pending">Pendiente</option>
            <option value="paid">Pagada</option>
            <option value="overdue">Vencida</option>
            <option value="cancelled">Anulada</option>
          </select>
        </div>
      </div>
      
      {/* Client Selector */}
      <div>
        <label htmlFor="clientSelector" className="block text-sm font-medium text-gray-700 mb-1">
          Cliente
        </label>
        <ClientSelector
          value={clientId}
          onChange={handleClientSelectorChange}
          placeholder="Seleccionar cliente (opcional)"
        />
        <p className="mt-1 text-xs text-gray-500">
          Al seleccionar un cliente, se llenarán automáticamente el RUT y la razón social.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Client RUT */}
        <div>
          <label htmlFor="clientRut" className="block text-sm font-medium text-gray-700 mb-1">
            RUT Cliente <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="clientRut"
            value={clientRut}
            onChange={(e) => {
              setClientRut(e.target.value);
              handleFieldChange('clientRut', e.target.value);
            }}
            onBlur={handleRutBlur}
            placeholder="12.345.678-9"
            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              errors.clientRut ? 'border-red-500' : 'border-gray-300'
            }`}
          />
          {errors.clientRut && (
            <p className="mt-1 text-sm text-red-600">{errors.clientRut}</p>
          )}
        </div>
        
        {/* Client Name */}
        <div>
          <label htmlFor="clientName" className="block text-sm font-medium text-gray-700 mb-1">
            Razón Social <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="clientName"
            value={clientName}
            onChange={(e) => {
              setClientName(e.target.value);
              handleFieldChange('clientName', e.target.value);
            }}
            placeholder="Nombre del cliente"
            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              errors.clientName ? 'border-red-500' : 'border-gray-300'
            }`}
          />
          {errors.clientName && (
            <p className="mt-1 text-sm text-red-600">{errors.clientName}</p>
          )}
        </div>
      </div>
      
      {/* Amount */}
      <div>
        <label htmlFor="totalAmount" className="block text-sm font-medium text-gray-700 mb-1">
          Monto Total (incluye IVA) <span className="text-red-500">*</span>
        </label>
        <input
          type="number"
          id="totalAmount"
          value={totalAmount}
          onChange={(e) => {
            setTotalAmount(e.target.value);
            handleFieldChange('totalAmount', e.target.value);
          }}
          placeholder="0.00"
          step="0.01"
          min="0"
          className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
            errors.totalAmount ? 'border-red-500' : 'border-gray-300'
          }`}
        />
        {errors.totalAmount && (
          <p className="mt-1 text-sm text-red-600">{errors.totalAmount}</p>
        )}
        {totalAmount && !errors.totalAmount && (
          <p className="mt-1 text-xs text-gray-500">
            Neto: {formatCurrency(parseFloat(totalAmount) / 1.19)} | IVA: {formatCurrency(parseFloat(totalAmount) - (parseFloat(totalAmount) / 1.19))}
          </p>
        )}
      </div>
      
      {/* Description */}
      <div>
        <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
          Descripción <span className="text-red-500">*</span>
        </label>
        <textarea
          id="description"
          value={description}
          onChange={(e) => {
            setDescription(e.target.value);
            handleFieldChange('description', e.target.value);
          }}
          placeholder="Descripción de la factura"
          rows={3}
          className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
            errors.description ? 'border-red-500' : 'border-gray-300'
          }`}
        />
        {errors.description && (
          <p className="mt-1 text-sm text-red-600">{errors.description}</p>
        )}
      </div>
      
      {/* Error message */}
      {(createInvoice.isError || updateInvoice.isError) && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {(createInvoice.error as Error)?.message || (updateInvoice.error as Error)?.message || 'Error al guardar la factura'}
        </div>
      )}
      
      {/* Actions */}
      <div className="flex flex-col sm:flex-row gap-3 justify-end">
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            disabled={isSaving}
            className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
          >
            Cancelar
          </button>
        )}
        <button
          type="submit"
          disabled={isSaving}
          className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 flex items-center justify-center"
        >
          {isSaving ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              Guardando...
            </>
          ) : (
            isEditing ? 'Actualizar Factura' : 'Crear Factura'
          )}
        </button>
      </div>
    </form>
  );
};

export default InvoiceForm;
